// src/pages/TopicDetail.jsx
import React, { useEffect, useState } from "react";
import { db } from "./firebase/firebaseConfig";
import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { useParams } from "react-router-dom";
import styled from "styled-components";

const Wrapper = styled.div`
  padding: 40px;
  color: #fff;
  max-width: 800px;
  margin: auto;
`;

const TextArea = styled.textarea`
  width: 100%;
  margin-bottom: 15px;
  padding: 10px;
  border-radius: 8px;
`;

const Button = styled.button`
  padding: 10px 16px;
  background: #00d4ff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-right: 10px;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 20px;
  border-radius: 10px;
`;

const TopicDetail = () => {
  const { prepId, moduleId, topicId } = useParams();

  const [topicName, setTopicName] = useState("");
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const [explanation, setExplanation] = useState("");
  const [example, setExample] = useState("");
  const [interviewQA, setInterviewQA] = useState({ question: "", answer: "" });
  const [codingQ, setCodingQ] = useState("");

  const [contentId, setContentId] = useState(null);

  // ✅ Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const topicSnap = await getDoc(
          doc(
            db,
            `interviewPreps/${prepId}/modules/${moduleId}/topics`,
            topicId
          )
        );

        if (topicSnap.exists()) {
          setTopicName(topicSnap.data().title || "");
        }

        const contentSnap = await getDocs(
          collection(
            db,
            `interviewPreps/${prepId}/modules/${moduleId}/topics/${topicId}/contents`
          )
        );

        if (!contentSnap.empty) {
          const docData = contentSnap.docs[0];
          const data = docData.data();

          setContentId(docData.id);
          setExplanation(data.explanation || "");
          setExample(data.example || "");
          setInterviewQA(data.interviewQA || { question: "", answer: "" });
          setCodingQ(data.codingQuestion || "");
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchData();
  }, [prepId, moduleId, topicId]);

  // ✅ AI Generate (SAFE)
  const handleGenerateAI = async () => {
    if (!topicName) return alert("Topic not loaded");

    setLoading(true);

    try {
      const res = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
        },
        body: JSON.stringify({
          model: "gpt-4.1-mini",
          response_format: { type: "json_object" },
          messages: [
            {
              role: "user",
              content: `Generate interview prep content for ${topicName}`,
            },
          ],
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.error?.message);
        return;
      }

      let text = data?.choices?.[0]?.message?.content || "";

      // clean
      text = text
        .replace(/```json/g, "")
        .replace(/```/g, "")
        .trim();

      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch {
        alert("Invalid JSON from AI");
        return;
      }

      setExplanation(parsed?.explanation || "");
      setExample(parsed?.example || "");
      setInterviewQA(parsed?.interviewQA || { question: "", answer: "" });
      setCodingQ(parsed?.codingQuestion || "");
    } catch (err) {
      console.error(err);
      alert("AI failed");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Save / Update
  const handleSave = async () => {
    try {
      const payload = {
        explanation,
        example,
        interviewQA,
        codingQuestion: codingQ,
        updatedAt: serverTimestamp(),
      };

      if (contentId) {
        await updateDoc(
          doc(
            db,
            `interviewPreps/${prepId}/modules/${moduleId}/topics/${topicId}/contents`,
            contentId
          ),
          payload
        );
        alert("✅ Updated");
      } else {
        const ref = await addDoc(
          collection(
            db,
            `interviewPreps/${prepId}/modules/${moduleId}/topics/${topicId}/contents`
          ),
          {
            ...payload,
            createdAt: serverTimestamp(),
          }
        );
        setContentId(ref.id);
        alert("✅ Saved");
      }
    } catch (err) {
      console.error(err);
      alert("❌ Failed");
    }
  };

  // ✅ SAFE RENDER (object OR array)
  const renderQA = () => {
    if (!interviewQA) return <p>-</p>;

    // array support
    if (Array.isArray(interviewQA)) {
      return interviewQA.map((qa, i) => (
        <div key={i}>
          <p>
            <strong>Q:</strong> {qa?.question || "-"}
          </p>
          <p>
            <strong>A:</strong> {qa?.answer || "-"}
          </p>
        </div>
      ));
    }

    // object support
    return (
      <>
        <p>
          <strong>Q:</strong> {interviewQA?.question || "-"}
        </p>
        <p>
          <strong>A:</strong> {interviewQA?.answer || "-"}
        </p>
      </>
    );
  };

  return (
    <Wrapper>
      <h2>📘 {topicName || "Topic Content"}</h2>

      <div style={{ marginBottom: 20 }}>
        <Button onClick={handleGenerateAI}>
          {loading ? "Generating..." : "🤖 AI"}
        </Button>

        <Button onClick={() => setIsEditMode(!isEditMode)}>
          {isEditMode ? "👁 View" : "✏️ Edit"}
        </Button>

        {isEditMode && <Button onClick={handleSave}>💾 Save</Button>}
      </div>

      {/* VIEW MODE */}
      {!isEditMode && (
        <Card>
          <h3>📖 Explanation</h3>
          <p>{explanation || "-"}</p>

          <h3>💡 Example</h3>
          <pre>{example || "-"}</pre>

          <h3>❓ Interview</h3>
          {renderQA()}

          <h3>💻 Coding</h3>
          <pre>{codingQ || "-"}</pre>
        </Card>
      )}

      {/* EDIT MODE */}
      {isEditMode && (
        <>
          <h3>Explanation</h3>
          <TextArea
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
          />

          <h3>Example</h3>
          <TextArea
            value={example}
            onChange={(e) => setExample(e.target.value)}
          />

          <h3>Interview</h3>
          <TextArea
            placeholder="Question"
            value={interviewQA?.question || ""}
            onChange={(e) =>
              setInterviewQA((prev) => ({ ...prev, question: e.target.value }))
            }
          />
          <TextArea
            placeholder="Answer"
            value={interviewQA?.answer || ""}
            onChange={(e) =>
              setInterviewQA((prev) => ({ ...prev, answer: e.target.value }))
            }
          />

          <h3>Coding</h3>
          <TextArea
            value={codingQ}
            onChange={(e) => setCodingQ(e.target.value)}
          />
        </>
      )}
    </Wrapper>
  );
};

export default TopicDetail;
